import { useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { Box, IconButton, InputAdornment } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import { Button, Chip, Stack, TextField } from 'ui/baseComponents'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { DataTable } from 'ui/components/DataTable'
import { UPDATE_ORG } from 'ui/components/OrgList/mutations'
import { useCallback, useContext, useMemo, useState } from 'react'
import { MILLISECONDS_IN_SECONDS } from 'utilities/time'
import { UserContext } from 'ui/contexts'
import {
  ORG_TABLE_HEADERS,
  ORG_TABLE_NO_RESULTS,
} from 'ui/screens/Organizations/consts'
import { colors } from 'theme/colors'

const OrganizationsTable = ({ orgList, setOrgChangeError }) => {
  const STYLES = {
    outerWrapper: {
      paddingTop: '1rem',
      backgroundColor: colors.COOL[100],
    },
    innerWrapper: {
      padding: '1rem',
    },
    orgCount: {
      color: colors.GREY[700],
    },
    viewButton: {
      border: '1px solid !important',
    },
  }
  const TIMEOUT_IN_SECONDS = 10

  const { defaultMembership } = useContext(UserContext)
  const [timeoutId, setTimeoutId] = useState(false)
  const navigate = useNavigate()

  const [updateOrgMutation] = useMutation(UPDATE_ORG, {
    onError: (error) => {
      console.error(error)
      clearTimeout(timeoutId)
      setOrgChangeError('Unable to update default organization')
      setTimeoutId(
        setTimeout(
          () => setOrgChangeError(null),
          TIMEOUT_IN_SECONDS * MILLISECONDS_IN_SECONDS,
        ),
      )
    },
  })

  const updateOrg = useCallback(
    async (newOrgID) => {
      try {
        await updateOrgMutation({
          variables: {
            input: {
              orgId: newOrgID,
            },
          },
        })
        navigate('/patients')
      } catch (error) {
        console.error(error)
        setOrgChangeError(error)
      }
    },
    [navigate, updateOrgMutation, setOrgChangeError],
  )

  const columns = [
    {
      name: 'metaData',
      options: {
        display: false,
      },
    },
    {
      name: ORG_TABLE_HEADERS.name,
      options: {
        sort: true,
        setCellProps: () => ({
          'data-cy': 'org-list-row-name-count',
        }),
        customBodyRender: (value) => (
          <>
            {value.orgName}{' '}
            <span style={STYLES.orgCount}>({value.patientCount})</span>{' '}
            {value.defaultMembershipId === value.id ? (
              <Chip
                label="default"
                size="small"
                color="primary"
                data-cy="chip-default-org"
              />
            ) : null}
          </>
        ),
        sortCompare:
          (order) =>
          ({ data: name1 }, { data: name2 }) =>
            name1?.orgName.trim().localeCompare(name2?.orgName.trim()) *
            (order === 'asc' ? 1 : -1),
      },
    },
    {
      name: ORG_TABLE_HEADERS.id,
      options: {
        sort: false,
        setCellProps: () => ({
          'data-cy': 'org-list-row-id',
        }),
        customBodyRender: (value) => (
          <Stack direction="row" sx={{ minWidth: 300 }}>
            {value}{' '}
            <IconButton
              color="primary"
              size="small"
              onClick={() => navigator.clipboard.writeText(value)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    },
    {
      name: ORG_TABLE_HEADERS.tags,
      options: {
        sort: false,
        setCellProps: () => ({
          'data-cy': 'org-list-row-tags',
        }),
        customBodyRender: (value) => (
          <Stack direction="row">
            {value.tags?.map((tag) => (
              <Chip label={tag} key={`org-${value.orgName}-tag-${tag}`} />
            ))}
          </Stack>
        ),
      },
    },
    {
      name: ORG_TABLE_HEADERS.view,
      options: {
        sort: false,
        searchable: false,
        setCellProps: () => ({
          'data-cy': 'org-list-row-view-patients',
        }),
        customBodyRender: (value) => (
          <Stack alignItems="end" sx={{ width: 100 }}>
            <Button
              type="secondary"
              size="small"
              onClick={() => updateOrg(value)}
              data-cy="btn-org-list-view-patients"
              sx={STYLES.viewButton}
              title="View patients and set organization as default"
            >
              View
            </Button>
          </Stack>
        ),
      },
    },
    {
      name: 'hiddenName',
      options: {
        setCellProps: () => ({
          style: {
            display: 'none',
          },
        }),
        setCellHeaderProps: () => ({
          style: {
            display: 'none',
          },
        }),
      },
    },
    {
      name: 'hiddenTags',
      options: {
        setCellProps: () => ({
          style: {
            display: 'none',
          },
        }),
        setCellHeaderProps: () => ({
          style: {
            display: 'none',
          },
        }),
      },
    },
  ]

  const rows = useMemo(
    () =>
      orgList.map((org) => {
        const { id, patientCount, displayName, orgTags } = org

        return {
          [ORG_TABLE_HEADERS.name]: {
            orgName: displayName,
            defaultMembershipId: defaultMembership?.org?.id,
            id,
            patientCount,
          },
          [ORG_TABLE_HEADERS.id]: id,
          [ORG_TABLE_HEADERS.tags]: { tags: orgTags, orgName: displayName },
          [ORG_TABLE_HEADERS.view]: id,
          hiddenName: displayName,
          hiddenTags: orgTags.join(' '),
        }
      }),
    [orgList, defaultMembership],
  )

  const options = {
    filter: false,
    viewColumns: false,
    rowsPerPage: 100,
    rowsPerPageOptions: [50, 100, 200],
    textLabels: {
      body: {
        noMatch: ORG_TABLE_NO_RESULTS,
      },
    },
    search: true,
    searchOpen: true,
    searchAlwaysOpen: true,
    customSearchRender: (searchText, handleSearch) => {
      const handleTextChange = (e) => {
        handleSearch(e.target.value)
      }
      return (
        <Stack direction="row" spacing={2} justifyContent="end">
          <TextField
            placeholder="Filter by name, ID, or tag"
            size="small"
            value={searchText || ''}
            onChange={handleTextChange}
            variant="outlined"
            sx={{ width: '630px' }}
            inputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      )
    },
  }

  return (
    <Box data-cy="table-org-list">
      <DataTable columns={columns} data={rows} options={options} />
    </Box>
  )
}

export default OrganizationsTable
